import React, { useMemo } from "react"
import QRCode from "qrcode-generator"
import styles from "./CustomQRCode.module.css"
import { calculateDashArray } from "./utils/visuals"

// Constants to keep proportions consistent across different "size" prop values
const PROPORTIONS = {
  OUTER_STROKE_WIDTH: 1 / 40,
  QR_CODE_SIZE: 0.6,
  RING_SPACING: 0.06,
  RING_OUTER_RADIUS: 0.45,
  RING_INNER_STROKE: 1 / 80,
  RING_MIDDLE_STROKE: 1 / 60,
  ANCHOR_OUTER_CELL_RATIO: 3, // Outer anchor radius = 3 × cell size
  ANCHOR_INNER_CELL_RATIO: 1.5, // Inner anchor radius = 1.5 × cell size
  ANCHOR_STROKE: 1 / 80,
  DOT_SIZE: 1 / 2.5,
} as const

interface TQRCodeProps {
  data: string
  size: number
  gradientColors: string[]
}

const TQRCode = ({ data, size, gradientColors }: TQRCodeProps) => {
  const outerStrokeWidth = size * PROPORTIONS.OUTER_STROKE_WIDTH
  const strokePadding = outerStrokeWidth / 2
  const adjustedSize = size - outerStrokeWidth
  const qrCodeSizeInner = adjustedSize * PROPORTIONS.QR_CODE_SIZE

  const qrCodePaths = useMemo(() => {
    if (!data) {
      return {
        dotsPath: "",
        anchorOuterPath: "",
        anchorMiddlePath: "",
        anchorInnerPath: "",
      }
    }

    try {
      const qr = QRCode(4, "M")
      qr.addData(data)
      qr.make()

      const moduleCount = qr.getModuleCount()
      const cellSize = qrCodeSizeInner / moduleCount

      const generatePaths = () => {
        const dotSegments: string[] = []
        const anchorOuter: string[] = []
        const anchorInner: string[] = []

        const anchorCenters = [
          { row: 3, col: 3 }, // top-left
          { row: 3, col: moduleCount - 4 }, // top-right
          { row: moduleCount - 4, col: 3 }, // bottom-left
        ]

        // eslint-disable-next-line no-restricted-syntax
        for (const center of anchorCenters) {
          const cx = center.col * cellSize + cellSize / 2
          const cy = center.row * cellSize + cellSize / 2

          // Calculate anchor sizes using cell-based proportions
          const outerR = cellSize * PROPORTIONS.ANCHOR_OUTER_CELL_RATIO
          const innerR = cellSize * PROPORTIONS.ANCHOR_INNER_CELL_RATIO

          // Outer ring (stroke only)
          anchorOuter.push(
            `M ${cx},${cy} m -${outerR},0 a ${outerR},${outerR} 0 1,0 ${outerR * 2},0 a ${outerR},${outerR} 0 1,0 -${outerR * 2},0`,
          )

          // Inner filled circle
          anchorInner.push(
            `M ${cx},${cy} m -${innerR},0 a ${innerR},${innerR} 0 1,0 ${innerR * 2},0 a ${innerR},${innerR} 0 1,0 -${innerR * 2},0`,
          )
        }

        // eslint-disable-next-line no-plusplus
        for (let row = 0; row < moduleCount; row++) {
          // eslint-disable-next-line no-plusplus
          for (let col = 0; col < moduleCount; col++) {
            const isAnchorArea = (row < 7 && col < 7)
              || (row < 7 && col >= moduleCount - 7)
              || (row >= moduleCount - 7 && col < 7)

            if (qr.isDark(row, col) && !isAnchorArea) {
              const cx = col * cellSize + cellSize / 2
              const cy = row * cellSize + cellSize / 2
              const dotRadius = cellSize * PROPORTIONS.DOT_SIZE

              dotSegments.push(
                `M ${cx - dotRadius},${cy} a ${dotRadius},${dotRadius} 0 1,0 ${dotRadius * 2},0 a ${dotRadius},${dotRadius} 0 1,0 -${dotRadius * 2},0`,
              )
            }
          }
        }

        return {
          dotsPath: dotSegments.join(" "),
          anchorOuterPath: anchorOuter.join(" "),
          anchorInnerPath: anchorInner.join(" "),
        }
      }

      return generatePaths()
    } catch (error) {
      console.error("Error generating QR code:", error)

      return {
        dotsPath: "",
        anchorOuterPath: "",
        anchorInnerPath: "",
      }
    }
  }, [data, qrCodeSizeInner])

  // Calculate dimensions for circular elements
  const centerX = size / 2
  const centerY = size / 2
  const outerRadius = adjustedSize * PROPORTIONS.RING_OUTER_RADIUS
  const ringSpacing = size * PROPORTIONS.RING_SPACING

  // Calculate dash arrays for each ring
  const innerRadius = outerRadius - ringSpacing
  const middleRadius = outerRadius
  const innerCircumference = 2 * Math.PI * innerRadius
  const middleCircumference = 2 * Math.PI * middleRadius
  const innerDashArray = calculateDashArray(innerRadius, 4)
  const middleDashArray = calculateDashArray(middleRadius, 2)

  const cssVariables = {
    "--dash-offset-inner": `${innerCircumference}`,
    "--dash-offset-middle": `${middleCircumference}`,
  } as React.CSSProperties

  return (
    <svg
      width={size}
      height={size}
      viewBox={`-${strokePadding} -${strokePadding} ${size + outerStrokeWidth} ${size + outerStrokeWidth}`}
      style={cssVariables}
    >
      <defs>
        <linearGradient id="ringGradient" gradientTransform="rotate(90)">
          <stop offset="0%" stopColor={gradientColors[0]} />
          <stop offset="50%" stopColor={gradientColors[1]} />
          <stop offset="100%" stopColor={gradientColors[2]} />
        </linearGradient>
        <linearGradient
          id="animatedGradient"
          gradientTransform="rotate(90)"
          className={styles.animatedGradient}
        >
          <stop
            offset="0%"
            stopColor={gradientColors[0]}
            className={styles.gradientStop1}
          />
          <stop
            offset="50%"
            stopColor={gradientColors[1]}
            className={styles.gradientStop2}
          />
          <stop
            offset="100%"
            stopColor={gradientColors[2]}
            className={styles.gradientStop3}
          />
        </linearGradient>
      </defs>

      {/* Concentric rings */}
      <circle
        cx={centerX}
        cy={centerY}
        r={innerRadius}
        className={`${styles.qrRing} ${styles.qrRingPulse} ${styles.qrRingDashedInner} ${styles.qrRingRotateFast}`}
        style={{
          stroke: "url(#animatedGradient)",
          strokeDasharray: innerDashArray,
          strokeWidth: size * PROPORTIONS.RING_INNER_STROKE,
          strokeLinecap: "round",
        }}
      />

      <circle
        cx={centerX}
        cy={centerY}
        r={middleRadius}
        className={`${styles.qrRingOuter} ${styles.qrRingDashedMiddle} ${styles.qrRingRotateSlow}`}
        style={{
          stroke: "url(#animatedGradient)",
          strokeDasharray: middleDashArray,
          strokeWidth: size * PROPORTIONS.RING_MIDDLE_STROKE,
          strokeLinecap: "round",
        }}
      />

      <circle
        cx={centerX}
        cy={centerY}
        r={outerRadius + ringSpacing}
        className={`${styles.qrRingOuter}`}
        style={{
          stroke: "url(#animatedGradient)",
          strokeWidth: outerStrokeWidth,
        }}
      />

      {/* QR code + anchors */}
      <g
        className={styles.qrContainer}
        transform={`translate(${(size - qrCodeSizeInner) / 2}, ${(size - qrCodeSizeInner) / 2})`}
      >
        <path d={qrCodePaths.dotsPath} className={styles.qrDot} />
        <path
          d={qrCodePaths.anchorOuterPath}
          className={styles.qrAnchorOuter}
          style={{
            strokeWidth: size * PROPORTIONS.ANCHOR_STROKE,
            fill: "none",
          }}
        />
        <path
          d={qrCodePaths.anchorInnerPath}
          className={styles.qrAnchorInner}
        />
      </g>
    </svg>
  )
}

export default TQRCode
